import React, { useState, useEffect, useRef } from 'react';
import { Search, Play, Pause, ChevronLeft, ChevronRight, User, Calendar } from 'lucide-react';

const VoiceBrowser = () => {
  const [voices, setVoices] = useState([]);
  const [filteredVoices, setFilteredVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const audioRef = useRef(null);
  
  const voicesPerPage = 4;
  const API_KEY = "";
  // Fetch voices from ElevenLabs API
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.elevenlabs.io/v1/shared-voices?search=singapore',
        {
          headers: { "xi-api-key": API_KEY },
        }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setVoices(data.voices || []);
        setFilteredVoices(data.voices || []);
      } catch (err) {
        setError('Failed to fetch voices: ' + err.message);
        console.error('Error fetching voices:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVoices();
  }, []);

  // Filter voices based on search term
  useEffect(() => {
    const filtered = voices.filter(voice => 
      voice.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voice.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voice.locale?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      voice.accent?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredVoices(filtered);
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, voices]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredVoices.length / voicesPerPage);
  const startIndex = (currentPage - 1) * voicesPerPage;
  const endIndex = startIndex + voicesPerPage;
  const currentVoices = filteredVoices.slice(startIndex, endIndex);

  // Handle voice selection
  const handleVoiceSelect = (voice) => {
    setSelectedVoice(voice);
  };

  // Audio preview functionality
  const handlePlayPreview = async (voice) => {
    if (playingVoice === voice.voice_id) {
      // Stop current audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingVoice(null);
      return;
    }

    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
      }

      // Note: This is a placeholder for the actual preview functionality
      // In a real implementation, you would need to:
      // 1. Have an ElevenLabs API key
      // 2. Make a request to the text-to-speech endpoint
      // 3. Play the returned audio
      
      setPlayingVoice(voice.voice_id);
      
      // Simulate audio playback (remove this in real implementation)
      setTimeout(() => {
        setPlayingVoice(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error playing preview:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

    if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading voices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Voices</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-left mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">ElevenLabs Voice Library</h1>
          <p className="text-gray-600">Select one voices from the community</p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md  mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search voices by name, description, or accent..."
              value={searchTerm}
              onChange={(e) => {setSearchTerm(e.target.value); setSelectedVoice(null)}}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Selected Voice Info */}
        {/* {selectedVoice && (
          <div className="max-w-2xl mx-auto mb-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-900">Selected Voice</h3>
              <button
                onClick={() => setSelectedVoice(null)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Clear Selection
              </button>
            </div>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg mr-4">
                {selectedVoice.name?.charAt(0)?.toUpperCase() || 'V'}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900">{selectedVoice.name || 'Unnamed Voice'}</h4>
                <p className="text-blue-700 text-sm">{selectedVoice.description || 'No description available'}</p>
                <div className="flex items-center mt-2 space-x-2">
                  {selectedVoice.labels?.gender && (
                    <span className="bg-blue-200 text-blue-800 text-xs px-2 py-1 rounded-full">
                      {selectedVoice.labels.gender}
                    </span>
                  )}
                  {selectedVoice.labels?.accent && (
                    <span className="bg-blue-200 text-blue-800 text-xs px-2 py-1 rounded-full">
                      {selectedVoice.labels.accent}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* Results Info */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredVoices.length)} of {filteredVoices.length} voices
          </p>
          <div className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </div>
        </div>

        {/* Voice Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5  mb-8">
          {currentVoices.map((voice) => (
            <div >
            <div 
              key={voice.voice_id} 
              className={`flex flex-col h-full  bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-6 cursor-pointer border-2 relative ${
                selectedVoice?.voice_id === voice.voice_id 
                  ? 'border-blue-500 ring-2 ring-blue-200' 
                  : 'border-transparent hover:border-gray-200'
              }`}
              onClick={() => handleVoiceSelect(voice)}
            >
              {/* Selection Indicator */}
              {selectedVoice?.voice_id === voice.voice_id && (
                <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Voice Avatar */}
              <div className="mb-4">
                {/* <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                  {voice.name?.charAt(0)?.toUpperCase() || 'V'}
                </div> */}
                <div >
                  <h3 className="font-semibold text-gray-900 ">{voice.name || 'Unnamed Voice'}</h3>
                  
                </div>
              </div>

              {/* Voice Info */}
              <div className="space-y-2 mb-4">
                {voice.locale && (
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    {voice.locale}
                  </span>
                )}
                {voice.accent && (
                  <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full ml-2">
                    {voice.accent}
                  </span>
                )}
                {voice.labels?.age && (
                  <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full ml-2">
                    {voice.labels.age}
                  </span>
                )}
              </div>

              {/* Description */}
              {voice.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {voice.description}
                </p>
              )}

              {/* Date */}
              {voice.date_unix && (
                <p className="text-xs text-gray-400 mb-4 flex items-center grow">
                  <Calendar className="w-3 h-3 mr-1" />
                  Added {formatDate(new Date(voice.date_unix * 1000))}
                </p>
              )}

              {/* Preview Button */}
              <div className="mt-4">
            {voice.preview_url ? (
              <audio
                controls
                src={voice.preview_url}
                className="w-full"
              />
            ) : (
              <p className="text-sm text-gray-500">No sample available</p>
            )}
           </div> 
		  </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredVoices.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No voices found</h3>
            <p className="text-gray-600">Try adjusting your search terms</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </button>
            
            <div className="flex space-x-2">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNumber = Math.max(1, currentPage - 2) + i;
                if (pageNumber > totalPages) return null;
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      currentPage === pageNumber
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceBrowser;
